<template>
  <b-card
    no-body
    class="card-statistics"
  >
    <b-card-header>
      <b-card-title>{{ $t('Loan Portfolio Summary') }}</b-card-title>
      <feather-icon
        icon="RotateCwIcon"
        class="cursor-pointer"
        @click="fetchLoanSummaries"
      />
    </b-card-header>
    <b-card-body class="statistics-body">
      <b-row>
        <b-col
          v-for="(item, index) in loanSummary"
          :key="index.toString()"
          xl="3"
          sm="6"
          :class="index > 3 ? 'mt-2' : '' && item.customClass"
        >
          <b-media no-body>
            <b-media-aside
              class="mr-1"
            >
              <b-avatar
                size="30"
                :variant="item.color"
              >
                <feather-icon
                  size="20"
                  :icon="item.icon"
                />
              </b-avatar>
            </b-media-aside>
            <b-media-body class="my-auto">
              <h5 class="font-weight-bolder mb-0 text-wrap">
                {{ item.title }}
              </h5>
              <b-card-text class="font-small-3 mb-0">
                {{ $t(item.subtitle) }}
              </b-card-text>
            </b-media-body>
          </b-media>
        </b-col>
      </b-row>
    </b-card-body>
    <Spinner :show="isLoadingLoanSummary" />
  </b-card>
</template>

<script>
import { onMounted, ref } from '@vue/composition-api'
import {
  BAvatar,
  BCard,
  BCardBody,
  BCardHeader,
  BCardText,
  BCardTitle,
  BCol, BMedia, BMediaAside,
  BMediaBody,
  BRow,
} from 'bootstrap-vue'
import { useActions } from 'vuex-composition-helpers'
import Spinner from '@/views/components/spinner/Spinner.vue'
import { toBalance } from '@/helpers/formatAmount'
import useToastAlert from '../../../composable/useToast'
import addSpaceToString from '../../../helpers/addSpaceToString'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardText,
    BCardBody,
    BMedia,
    BAvatar,
    BMediaAside,
    BMediaBody,
    Spinner,
  },
  setup() {
    const { getLoanSummaries } = useActions({ getLoanSummaries: 'dashboard/getLoanSummaries' })
    const { toastAlert } = useToastAlert()
    const loanSummary = ref([])
    const isLoadingLoanSummary = ref(false)
    const getColor = item => {
      if (item === 'totalLoansDisbursed') {
        return 'light-primary'
      }
      if (item === 'totalPrincipalOutstanding') {
        return 'light-info'
      }
      if (item === 'totalInterestOutstanding') {
        return 'light-danger'
      }
      if (item === 'totalPrincipalPaid') {
        return 'light-success'
      }
      if (item === 'totalPrincipalDueAndUnpaid') {
        return 'light-warning'
      }
      if (item === 'totalPrincipalNotYetDue') {
        return 'light-secondary'
      }
      return 'light-success'
    }

    const getIcon = item => {
      if (item === 'totalLoansDisbursed') {
        return 'TrendingUpIcon' // Disbursement growth
      }
      if (item === 'totalPrincipalOutstanding') {
        return 'CreditCardIcon' // Outstanding debt/balance
      }
      if (item === 'totalInterestOutstanding') {
        return 'PercentIcon' // Interest percentage
      }
      if (item === 'totalPrincipalPaid') {
        return 'CheckCircleIcon' // Paid/completed
      }
      if (item === 'totalPrincipalDueAndUnpaid') {
        return 'AlertCircleIcon' // Warning - needs attention
      }
      if (item === 'totalPrincipalNotYetDue') {
        return 'ClockIcon' // Future/scheduled
      }
      return 'TrendingDownIcon'
    }

    const fetchLoanSummaries = async () => {
      try {
        isLoadingLoanSummary.value = true
        const response = await getLoanSummaries()

        const { status, data } = response
        if (status === 200 && data) {
          loanSummary.value = Object.entries({ ...data }).map(([key, value]) => ({
            icon: getIcon(key),
            color: getColor(key),
            title: toBalance(value),
            subtitle: addSpaceToString(key),
            customClass: 'mb-2 mb-xl-0',
            ...value,
          }))
          isLoadingLoanSummary.value = false
        }
      } catch (err) {
        toastAlert(err)
      } finally {
        isLoadingLoanSummary.value = false
      }
    }

    onMounted(async () => {
      await fetchLoanSummaries()
    })

    return {
      loanSummary,
      fetchLoanSummaries,
      isLoadingLoanSummary,
    }
  },
}
</script>
